import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronRight, ChevronLeft, BookOpen, Brain, LayoutDashboard, BookMarked, Sparkles, FileText } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const WALKTHROUGH_KEY = 'onboarding_walkthrough_dismissed';
const WALKTHROUGH_SEEN_KEY = 'onboarding_walkthrough_seen_users';

const steps = [
  {
    id: 'welcome',
    icon: Sparkles,
    title: 'Welcome to Fortified Mind',
    description: 'You\'re all set! Let\'s take a quick tour of the key features to help you get started with your scripture memorization journey.',
    highlight: null,
    route: null,
    color: 'from-primary/20 to-accent/20',
    iconColor: 'text-primary',
  },
  {
    id: 'dashboard',
    icon: LayoutDashboard,
    title: 'Your Dashboard',
    description: 'Your home base. Track your daily streak, review progress, access your daily verse, and see recent activity — all at a glance.',
    highlight: 'dashboard',
    route: '/dashboard',
    color: 'from-primary/15 to-primary/5',
    iconColor: 'text-primary',
  },
  {
    id: 'verse-library',
    icon: BookMarked,
    title: 'Verse Library',
    description: 'Add and organize the scriptures you want to memorize. Filter by book, phase, or tag. Your personal collection of verses to master.',
    highlight: 'verse-library',
    route: '/verse-library',
    color: 'from-accent/20 to-accent/5',
    iconColor: 'text-accent',
  },
  {
    id: 'practice',
    icon: Brain,
    title: 'Practice Modes',
    description: 'Strengthen your memory with multiple practice modes — fill-in-the-blank, full recall, audio, and progressive chunking — all powered by spaced repetition.',
    highlight: 'memorization-practice',
    route: '/memorization-practice',
    color: 'from-success/20 to-success/5',
    iconColor: 'text-success',
  },
  {
    id: 'reading-plans',
    icon: BookOpen,
    title: 'Reading Plans',
    description: 'Follow structured Bible reading plans to build consistent habits. Choose from chronological, topical, or custom plans and track your progress daily.',
    highlight: 'bible-reading-plans',
    route: '/bible-reading-plans',
    color: 'from-warning/20 to-warning/5',
    iconColor: 'text-warning',
  },
  {
    id: 'study-notes',
    icon: FileText,
    title: 'Study & Sermon Notes',
    description: 'Create and present sermon or study notes with slide-based layouts. Share live presentations with your congregation via a QR code — audience members can follow along in real time on their phones.',
    highlight: 'study-notes',
    route: '/tools/notes',
    color: 'from-purple-500/20 to-purple-500/5',
    iconColor: 'text-purple-500',
  },
];

const OnboardingWalkthrough = ({ onComplete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (!user?.id) return;

    // Check if this user has permanently dismissed
    const dismissedUsers = JSON.parse(localStorage.getItem(WALKTHROUGH_KEY) || '[]');
    if (dismissedUsers?.includes(user?.id)) return;

    // Show walkthrough
    setTimeout(() => setIsVisible(true), 600);
  }, [user?.id]);

  const dismiss = useCallback((permanent = false) => {
    if (permanent && user?.id) {
      const dismissedUsers = JSON.parse(localStorage.getItem(WALKTHROUGH_KEY) || '[]');
      if (!dismissedUsers?.includes(user?.id)) {
        dismissedUsers?.push(user?.id);
        localStorage.setItem(WALKTHROUGH_KEY, JSON.stringify(dismissedUsers));
      }
    }
    setIsAnimating(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsAnimating(false);
      onComplete?.();
    }, 300);
  }, [user?.id, onComplete]);

  const handleNext = () => {
    if (currentStep < steps?.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleFinish = () => {
    dismiss(dontShowAgain);
  };

  const handleSkip = () => {
    dismiss(dontShowAgain);
  };

  const handleGoToFeature = () => {
    const step = steps?.[currentStep];
    if (step?.route) {
      dismiss(dontShowAgain);
      navigate(step?.route);
    }
  };

  if (!isVisible) return null;

  const step = steps?.[currentStep];
  const StepIcon = step?.icon;
  const isLastStep = currentStep === steps?.length - 1;
  const isFirstStep = currentStep === 0;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center p-4 transition-all duration-300 ${
        isAnimating ? 'opacity-0 scale-95' : 'opacity-100 scale-100'
      }`}
      style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}
    >
      <div className="relative w-full max-w-md bg-card border border-border rounded-2xl shadow-2xl overflow-hidden">
        {/* Top gradient bar */}
        <div className={`h-1.5 w-full bg-gradient-to-r ${step?.color}`} />

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-2">
          <div className="flex items-center gap-2">
            {steps?.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentStep(idx)}
                className={`transition-all duration-200 rounded-full ${
                  idx === currentStep
                    ? 'w-6 h-2 bg-primary'
                    : idx < currentStep
                    ? 'w-2 h-2 bg-primary/50' :'w-2 h-2 bg-border'
                }`}
                aria-label={`Go to step ${idx + 1}`}
              />
            ))}
          </div>
          <button
            onClick={handleSkip}
            className="text-muted-foreground hover:text-foreground transition-colors p-1 rounded-lg hover:bg-muted"
            aria-label="Close walkthrough"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${step?.color} flex items-center justify-center mb-4 border border-border/50`}>
            <StepIcon size={26} className={step?.iconColor} />
          </div>

          <div className="mb-1 text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {isFirstStep ? 'Getting Started' : `Feature ${currentStep} of ${steps?.length - 1}`}
          </div>
          <h2 className="text-xl font-semibold text-foreground mb-3 leading-tight">
            {step?.title}
          </h2>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {step?.description}
          </p>

          {/* Go to feature button (not on welcome step) */}
          {step?.route && (
            <button
              onClick={handleGoToFeature}
              className="mt-4 text-xs font-medium text-primary hover:text-primary/80 underline underline-offset-2 transition-colors"
            >
              Take me there →
            </button>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-5 pt-2">
          {/* Don't show again */}
          <label className="flex items-center gap-2 mb-4 cursor-pointer group">
            <div
              onClick={() => setDontShowAgain(prev => !prev)}
              className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                dontShowAgain
                  ? 'bg-primary border-primary' :'border-border group-hover:border-primary/50'
              }`}
            >
              {dontShowAgain && (
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                  <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </div>
            <span className="text-xs text-muted-foreground group-hover:text-foreground transition-colors">
              Don't show this again
            </span>
          </label>

          {/* Navigation buttons */}
          <div className="flex items-center gap-3">
            {!isFirstStep && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1.5 px-4 py-2 text-sm text-muted-foreground hover:text-foreground border border-border rounded-lg hover:bg-muted transition-all"
              >
                <ChevronLeft size={15} />
                Back
              </button>
            )}

            <button
              onClick={isFirstStep ? handleNext : isLastStep ? handleFinish : handleNext}
              className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-all"
            >
              {isLastStep ? (
                <>
                  <Sparkles size={15} />
                  Get Started
                </>
              ) : (
                <>
                  {isFirstStep ? 'Start Tour' : 'Next'}
                  <ChevronRight size={15} />
                </>
              )}
            </button>

            {!isLastStep && !isFirstStep && (
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                Skip
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWalkthrough;
